import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { uploadStaging, aiExtractCertificate, createCertificate, getSites, applyCertRules, listCertRules, listBoardLayouts, getBoardLayout, listReferenceBrands } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UkAddressFields, formatAddress } from '../../components/UkAddressFields';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { fileUrl } from '../../services/api';
import { Camera, Sparkles, Loader2, Plus, Trash2, X, ShieldAlert, FileText, Wand2, LayoutPanelTop, Aperture, Brain, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { todayIso } from '../../lib/dateUtils';
import CameraWithLayoutOverlay, { FrameOverlay } from '../../components/CameraWithLayoutOverlay';
import CertCircuitCropPopover from '../../components/CertCircuitCropPopover';

const EMPTY_CIRCUIT = {
  number: '', description: '', type: '', cableSize: '', mcbType: 'B', mcbRating: '',
  rcdRating: '30mA', insulationResistance: '', earthLoopImpedance: '', rcdTripTime: '',
  polarity: 'Pass', notes: ''
};

// AI returns circuits in a NESTED shape per the prompt:
//   { number, description, ocpd: {type, ratingA, ...}, rcd: {ratedOperatingMa, ...} }
// The form binds to FLAT fields (mcbType, mcbRating, rcdRating). This helper
// maps nested → flat in one place so:
//   1. the form pre-fills correctly after `runAI()`
//   2. we can capture an `aiOriginal` snapshot in the same shape the engineer
//      will edit, so the backend can compare like-with-like and learn from
//      every manual correction.
const flattenAiCircuit = (c, idx) => {
  const ocpd = (c && typeof c.ocpd === 'object' && c.ocpd) || {};
  const rcd = (c && typeof c.rcd === 'object' && c.rcd) || {};
  const mcbRating = ocpd.ratingA != null && ocpd.ratingA !== '' ? `${ocpd.ratingA}A` : '';
  const rcdRating = rcd.ratedOperatingMa != null && rcd.ratedOperatingMa !== '' ? `${rcd.ratedOperatingMa}mA` : '';
  return {
    ...EMPTY_CIRCUIT,
    number: String(c?.number || (idx + 1)),
    description: String(c?.description || ''),
    mcbType: ocpd.type || 'B',
    mcbRating,
    rcdRating,
    earthLoopImpedance: ocpd.maxPermittedZs ? String(ocpd.maxPermittedZs) : '',
    // Pass-through frame mapping when the AI extracted with a layout overlay.
    frameId: typeof c?.frameId === 'string' ? c.frameId : '',
  };
};

/**
 * Pick the most recently captured photo's adjusted-frames array (if
 * any). When the engineer pinched/panned the overlay before saving,
 * we want the AI to see that final alignment — not the layout's
 * pristine coords. If no photo carries overrides we return null so
 * the backend falls back to the layout's stored frames.
 */
const collectFrameOverrides = (photos) => {
  const withOverride = [...photos].reverse().find(
    (p) => Array.isArray(p.adjustedFrames) && p.adjustedFrames.length > 0
  );
  return withOverride ? withOverride.adjustedFrames : null;
};

const CertNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const fileInputRef = useRef(null);
  const [photos, setPhotos] = useState([]);  // [{id, storedAs, url, filename, contentType}]
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [extra, setExtra] = useState('');
  // Snapshot of the AI's normalised circuits at the moment of extraction,
  // BEFORE any engineer edits. Sent back on save so the backend can record
  // every manual correction into the few-shot memory.
  const [aiOriginal, setAiOriginal] = useState([]);
  // Count of enabled cert rules in the system. Drives the "Apply rules"
  // button label + disabled-state so engineers see at a glance whether
  // any rules will run when they click.
  const [activeRuleCount, setActiveRuleCount] = useState(null);
  // Board-layout picker — when set, the AI extractor includes the
  // layout's frames in its prompt and the photo previews show a
  // semi-transparent overlay so the engineer can confirm alignment.
  const [layouts, setLayouts] = useState([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState('');
  // Reference Library brand. When set, every photo'd library item
  // under this brand is sent to the AI as a confirmed example so
  // extractions on this brand's boards / breakers / isolators are
  // anchored to the brand's actual visual fingerprint.
  const [referenceBrands, setReferenceBrands] = useState([]);
  const [selectedReferenceBrandId, setSelectedReferenceBrandId] = useState('');
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  // Photo currently being re-aligned (when null, the camera dialog
  // is in fresh-capture mode).
  const [alignPhoto, setAlignPhoto] = useState(null);
  const [form, setForm] = useState({
    certType: 'EICR',
    siteId: '',
    customerId: '',
    siteAddress: '',
    clientName: '',
    inspectorName: '',
    inspectorRegNumber: '',
    dateOfInspection: todayIso(),
    nextInspectionDate: '',
    supplyType: 'TN-C-S',
    nominalVoltage: '230V',
    nominalFrequency: '50Hz',
    earthFaultLoopImpedance: '',
    prospectiveFaultCurrent: '',
    overallCondition: 'Satisfactory',
    observations: '',
    circuits: [{ ...EMPTY_CIRCUIT, number: '1' }],
  });

  useEffect(() => { getSites().then(setSites); }, []);

  // Customer dropdown — derived from the sites list. Every site row
  // already carries `customerId` + `customerName` (populated by the
  // backend `/sites` route), so we don't need a separate
  // `getCustomers` call. Dedupe + sort alphabetically.
  const customerOptions = useMemo(() => {
    const m = new Map();
    for (const s of sites) {
      if (s.customerId && s.customerName) m.set(s.customerId, s.customerName);
    }
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sites]);
  useEffect(() => {
    listBoardLayouts().then(d => setLayouts(d?.layouts || [])).catch(() => setLayouts([]));
  }, []);
  // Load reference brands so the engineer can pre-pick one before
  // extraction. Brands with zero photo'd items are still shown so
  // the engineer knows the slot exists; the backend silently skips
  // them when their photo count is zero.
  useEffect(() => {
    listReferenceBrands().then(setReferenceBrands).catch(() => setReferenceBrands([]));
  }, []);
  // Hydrate the full layout (with frames) whenever the picker changes.
  // When the loaded layout has slots, prepopulate the cert's circuits
  // table — one row per slot, in cctId order, using the slot label as
  // the circuit number. Skips overwriting if the engineer has already
  // typed something into the existing rows.
  useEffect(() => {
    if (!selectedLayoutId) {
      setSelectedLayout(null);
      return;
    }
    getBoardLayout(selectedLayoutId)
      .then((lay) => {
        setSelectedLayout(lay);
        const slots = lay?.slots || [];
        if (!slots.length) return;
        setForm((f) => {
          const onlyDefault = (
            f.circuits.length <= 1 &&
            (f.circuits[0]?.number || '') === '1' &&
            !(f.circuits[0]?.description || '') &&
            !(f.circuits[0]?.cableSize || '') &&
            !(f.circuits[0]?.mcbRating || '')
          );
          if (!onlyDefault) return f; // engineer already started typing — leave it alone
          // Build a frame lookup by cctId so prepopulated rows can
          // carry the matching frameId from the layout.
          const frameByCct = new Map();
          (lay.frames || []).forEach((fr) => {
            if (fr.cctId) frameByCct.set(fr.cctId, fr.id);
          });
          return {
            ...f,
            circuits: slots.map((s) => ({
              ...EMPTY_CIRCUIT,
              number: s.label,
              frameId: frameByCct.get(s.cctId) || '',
            })),
          };
        });
      })
      .catch(() => {
        toast.error('Could not load that layout');
        setSelectedLayout(null);
      });
  }, [selectedLayoutId]);
  useEffect(() => {
    listCertRules('card')
      .then(res => {
        const rules = Array.isArray(res) ? res : (res?.rules || []);
        setActiveRuleCount(rules.filter(r => r.enabled !== false).length);
      })
      .catch(() => setActiveRuleCount(0));
  }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updCircuit = (i, k, v) => setForm(f => ({
    ...f,
    circuits: f.circuits.map((c, idx) => idx === i ? { ...c, [k]: v } : c)
  }));
  const addCircuit = () => setForm(f => ({
    ...f,
    circuits: [...f.circuits, { ...EMPTY_CIRCUIT, number: String(f.circuits.length + 1) }]
  }));
  const removeCircuit = (i) => setForm(f => ({
    ...f,
    circuits: f.circuits.filter((_, idx) => idx !== i)
  }));

  const onSelectFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        // iPhone Camera-app exports come in as `image/heic` (or sometimes
        // `application/octet-stream` from iOS Safari, in which case we
        // fall back to the filename suffix). Backend re-encodes to JPEG.
        const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
        const heicSuffix = /\.(heic|heif|heics|heifs)$/i.test(file.name || '');
        if (!allowedMimes.includes(file.type) && !heicSuffix) {
          toast.error(`${file.name}: only JPG/PNG/WEBP/HEIC supported for AI`);
          continue;
        }
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 15 MB)`);
          continue;
        }
        const meta = await uploadStaging(file);
        setPhotos(prev => [...prev, meta]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const removePhoto = (id) => setPhotos(p => p.filter(x => x.id !== id));

  const runAI = async () => {
    if (photos.length === 0) return toast.error('Upload at least one photo first');
    setExtracting(true);
    try {
      const res = await aiExtractCertificate(
        photos.map(p => p.storedAs),
        form.certType,
        extra,
        selectedLayoutId || null,
        // Engineer-aligned frame coords from the camera dialog. Falls
        // back to the layout's pristine frames on the backend when
        // null.
        collectFrameOverrides(photos),
        selectedReferenceBrandId || null,
      );
      const d = res.extracted || {};
      const flatCircuits = (Array.isArray(d.circuits) && d.circuits.length)
        ? d.circuits.map((c, i) => flattenAiCircuit(c, i))
        : [];
      setAiOriginal(flatCircuits);
      // ── Merge AI circuits into the existing slot-ordered table by
      // frameId ──────────────────────────────────────────────────────
      // The layout pre-populates `form.circuits` with one row per
      // slot in canonical cctId order, each row tagged with the
      // matching `frameId`. The AI returns its circuits in whatever
      // reading order it scanned the photo, but every circuit it
      // extracted carries its source `frameId` (the camera-overlay
      // pipeline puts frames in the prompt and asks the model to
      // echo the id back). Merge AI fields ONTO the matching row by
      // frameId — preserving the row order the engineer expects on
      // the test-results sheet (1L1, 1L2, …, 8L3). Without this the
      // values would land in arbitrary rows.
      setForm(f => {
        if (!flatCircuits.length) return f;
        const existing = f.circuits || [];
        const hasSlotTable = existing.some(c => c.frameId);
        if (!hasSlotTable) {
          // No layout / no pre-populated table → fall back to the
          // legacy "replace" behaviour. The AI's reading order is
          // the best guess we have.
          return { ...f, circuits: flatCircuits };
        }
        // Index AI rows by frameId for O(1) lookup, keeping a
        // separate bucket for any rows the AI returned without a
        // recognisable frameId so they don't get lost.
        const byFrame = new Map();
        const orphans = [];
        for (const ai of flatCircuits) {
          if (ai.frameId && !byFrame.has(ai.frameId)) byFrame.set(ai.frameId, ai);
          else orphans.push(ai);
        }
        const merged = existing.map((row) => {
          const ai = row.frameId ? byFrame.get(row.frameId) : null;
          if (!ai) return row;  // no AI data for this slot — keep blank row
          // AI fields win over the empty pre-populated row; preserve
          // the row's `number` (= slot label like "1L1") because that
          // is what the engineer reads down the table.
          return { ...row, ...ai, number: row.number, frameId: row.frameId };
        });
        // Append any AI rows the layout didn't anticipate (extra
        // breakers, unmapped frames) at the end so the engineer can
        // reconcile them manually.
        return { ...f, circuits: [...merged, ...orphans] };
      });
      if (d._warning) toast.warning(d._warning);
      else toast.success(`AI pre-filled ${flatCircuits.length} circuit${flatCircuits.length === 1 ? '' : 's'} — please review and edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI extraction failed');
    } finally { setExtracting(false); }
  };

  /**
   * Run the cert rules engine against every circuit and return a copy
   * of the circuits array with derived values written into EMPTY
   * derivable fields. Engineer-typed values always win — we only
   * touch fields the engineer left blank.
   *
   * Returns `{circuits, applied}` where `applied` is the count of
   * fields actually written. Used both by the manual "Apply rules"
   * button and as an auto-step in `save()`.
   */
  const runRulesOverCircuits = async (circuits) => {
    const updated = [];
    let applied = 0;
    for (const c of circuits) {
      const ctx = {
        description: c.description || '',
        mcbType: c.mcbType || '',
        mcbRating: c.mcbRating || '',
        rcdRating: c.rcdRating || '',
        bsEn: c.bsEn || '',
        circuitNumber: c.number || '',
      };
      let derived = {};
      try {
        const res = await applyCertRules(ctx, 'card');
        derived = res.derived || {};
      } catch (e) {
        // Non-fatal — proceed without derivations for this circuit
        derived = {};
      }
      const next = { ...c };
      // Only fill cableSize and notes since those are the form fields
      // the engineer can see; other derivable fields (liveCableSize,
      // neutralCableSize, cpcSize) aren't yet rendered on the cert
      // form so we'd need to add UI for them — leaving for a future
      // pass to keep this change focused.
      if (!next.cableSize && derived.cableSize) {
        next.cableSize = derived.cableSize;
        applied += 1;
      }
      if (!next.cableSize && derived.liveCableSize) {
        // Fallback: if the engineer hasn't authored a cableSize rule
        // but has a liveCableSize one, use that (most common case
        // since liveCableSize is the more semantic field).
        next.cableSize = derived.liveCableSize;
        applied += 1;
      }
      if (!next.notes && derived.notes) {
        next.notes = derived.notes;
        applied += 1;
      } else if (next.notes && derived.notes && !next.notes.includes(derived.notes)) {
        // Append rule-derived note to existing engineer note (don't
        // overwrite). Common case: engineer wrote a custom note AND
        // a rule has a "double-check this" reminder.
        next.notes = `${next.notes}\n${derived.notes}`;
        applied += 1;
      }
      updated.push(next);
    }
    return { circuits: updated, applied };
  };

  const handleApplyRulesManual = async () => {
    if (!form.circuits || form.circuits.length === 0) {
      return toast.error('Add at least one circuit first');
    }
    if (activeRuleCount === 0) {
      return toast.error('No active rules defined. Create rules in Lists → Cert Rules first.', { duration: 5000 });
    }
    const { circuits: next, applied } = await runRulesOverCircuits(form.circuits);
    setForm(f => ({ ...f, circuits: next }));
    if (applied > 0) {
      toast.success(`Applied ${applied} rule derivation${applied === 1 ? '' : 's'} across ${next.length} circuit${next.length === 1 ? '' : 's'}`, { duration: 4000 });
    } else {
      toast.message(
        `Ran ${activeRuleCount ?? '?'} rule${activeRuleCount === 1 ? '' : 's'} — nothing changed. Either every derivable field is already filled or no rule matched the circuit values (check MCB rating / description).`,
        { duration: 6000 }
      );
    }
  };

  const save = async () => {
    if (!form.siteId) return toast.error('Select a site');
    setSaving(true);
    try {
      // Auto-apply rules before save so the saved cert reflects every
      // engineer-authored rule. Empty fields only — engineer's typed
      // values are never overwritten.
      const { circuits: enriched, applied } = await runRulesOverCircuits(form.circuits);
      const cert = await createCertificate({
        ...form,
        circuits: enriched,
        photoUrls: photos.map(p => p.url),
        aiAssisted: true,
        aiOriginal,
        // Persist the layout used so CertView can show a coverage
        // overlay on top of the source photos.
        layoutId: selectedLayoutId || null,
      });
      const tail = applied > 0 ? ` · ${applied} rule derivation${applied === 1 ? '' : 's'} applied` : '';
      toast.success(`Saved as draft — ${cert.id}${tail}`);
      navigate(`${base}/certificates/${cert.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="New Electrical Certificate"
        description="Upload photos, let AI pre-fill, then review every field before saving."
      />

      <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-red-700 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-800">
          <div className="font-semibold text-red-700">Engineer responsibility</div>
          <p>The AI is an assistant. Every value below must be verified by a competent qualified electrician before the certificate is issued. Drafts are clearly watermarked.</p>
        </div>
      </div>

      {/* Step 1: Photos + AI */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h2 className="font-display uppercase tracking-wide text-slate-900 mb-1">1. Photos for AI extraction</h2>
        <p className="text-sm text-slate-600 mb-4">Distribution board overview, individual MCBs / RCDs, test meter readings, and (optionally) a previous certificate page.</p>

        {/* Board layout picker — optional but enables both the camera
            overlay and a frame-aware AI prompt. */}
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
          <LayoutPanelTop className="h-5 w-5 text-blue-700 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-700">Board layout (optional)</Label>
            <Select
              value={selectedLayoutId || 'none'}
              onValueChange={(v) => setSelectedLayoutId(v === 'none' ? '' : v)}
            >
              <SelectTrigger className="mt-1 bg-white" data-testid="cert-layout-picker">
                <SelectValue placeholder="No layout — free-form photos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No layout — free-form photos</SelectItem>
                {layouts.map(l => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} · {l.frames?.length || 0} frame{l.frames?.length === 1 ? '' : 's'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
              Pick a saved board layout to overlay frames on the camera AND tell the AI exactly where each breaker / isolator sits in the photo.
            </p>
          </div>
          {selectedLayout && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCameraOpen(true)}
              data-testid="cert-camera-overlay-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Aperture className="h-4 w-4 mr-2" /> Capture with overlay
            </Button>
          )}
        </div>

        {/* Reference Library brand picker — when set, every photo'd
            library item under this brand is sent to GPT as a confirmed
            example so its readings on the site photo are anchored to
            the brand's actual visual fingerprint. Independent of the
            board layout above (engineer can use one, both, or neither). */}
        <div className="mb-4 bg-emerald-50/40 border border-emerald-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
          <BookOpen className="h-5 w-5 text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-slate-700">Reference Library brand (optional)</Label>
            <Select
              value={selectedReferenceBrandId || 'none'}
              onValueChange={(v) => setSelectedReferenceBrandId(v === 'none' ? '' : v)}
            >
              <SelectTrigger className="mt-1 bg-white" data-testid="cert-reference-brand-picker">
                <SelectValue placeholder="No reference brand — generic prompt" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No reference brand — generic prompt</SelectItem>
                {referenceBrands.map(b => {
                  const c = b.itemCounts || {};
                  const total = (c.breaker || 0) + (c.board || 0) + (c.isolator || 0);
                  return (
                    <SelectItem key={b.id} value={b.id} disabled={total === 0}>
                      {b.name} · {total} item{total === 1 ? '' : 's'}{total === 0 ? ' (empty)' : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
              Pick the consumer-unit's brand so the AI sees confirmed examples of that brand's breakers / boards before reading your site photo.
            </p>
          </div>
          <Link to="/admin/certificates/reference-library">
            <Button
              type="button"
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              data-testid="cert-reference-library-link"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Manage library
            </Button>
          </Link>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={e => onSelectFiles(e.target.files)} />
          <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Add photos'}
          </Button>
          {/* Hand the selected layout + uploaded photos straight to
              the Train AI page so the engineer can play the
              per-frame correction game without re-uploading. The
              training page reads `seedPhotoIds` + `seedLayoutId`
              from navigation state and auto-runs the layout-aware
              queue endpoint, slicing each photo into one mini-card
              per frame. Only enabled when both ingredients are in
              place. */}
          {selectedLayout && photos.length > 0 && (
            <Button
              type="button"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => navigate(`${base}/certificates/train`, {
                state: {
                  seedPhotoIds: photos.map(p => p.storedAs),
                  seedLayoutId: selectedLayoutId,
                  seedLayoutName: selectedLayout.name,
                },
              })}
              data-testid="send-to-train-ai-btn"
              title={`Slice each photo into ${selectedLayout.frames?.length || 0} frame-crops and play the correction game`}
            >
              <Brain className="h-4 w-4 mr-2" />
              Send to AI Training ({photos.length} photo{photos.length === 1 ? '' : 's'} × {selectedLayout.frames?.length || 0} frames)
            </Button>
          )}
          <span className="text-xs text-slate-500">JPG / PNG / WEBP up to 15 MB each</span>
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mt-4">
            {photos.map(p => (
              <div key={p.id} className="relative group">
                <div className="relative h-24 w-full rounded border border-slate-200 overflow-hidden">
                  <img src={fileUrl(p.url)} alt={p.filename} className="absolute inset-0 h-full w-full object-cover" />
                  {selectedLayout?.frames?.length > 0 && (
                    <FrameOverlay
                      frames={p.adjustedFrames || selectedLayout.frames}
                      opacity={0.5}
                    />
                  )}
                </div>
                {selectedLayout?.frames?.length > 0 && (
                  <button
                    onClick={() => { setAlignPhoto(p); setCameraOpen(true); }}
                    className="absolute bottom-1 left-1 bg-slate-900/80 hover:bg-slate-900 text-white text-[10px] font-medium rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                    title="Pinch / drag to align frames over this photo"
                    data-testid={`align-overlay-${p.id}`}
                  >
                    <Aperture className="h-3 w-3" /> Align
                  </button>
                )}
                <button onClick={() => removePhoto(p.id)} className="absolute -top-2 -right-2 h-6 w-6 bg-white border border-slate-300 rounded-full text-slate-600 hover:text-red-600 hover:border-red-300 shadow flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Label className="text-xs text-slate-600">Optional notes for the AI (specific things to look for)</Label>
          <Textarea rows={2} className="mt-1.5" value={extra} onChange={e => setExtra(e.target.value)} placeholder="e.g. Focus on the RCD trip times in row 2 of the consumer unit." />
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button onClick={runAI} disabled={extracting || photos.length === 0} className="bg-blue-700 hover:bg-blue-800 text-white font-semibold">
            {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {extracting ? 'Analysing photos...' : 'Run AI extraction'}
          </Button>
          <span className="text-xs text-slate-500">
            {photos.length} photo{photos.length === 1 ? '' : 's'} ready
            {selectedLayout && ` · using layout "${selectedLayout.name}"`}
          </span>
        </div>
      </section>

      {/* Camera-with-overlay dialog — handles both fresh capture
          (no initialPhoto) and "Align overlay over an existing
          photo" (initialPhoto set). */}
      <CameraWithLayoutOverlay
        open={cameraOpen}
        onOpenChange={(o) => {
          setCameraOpen(o);
          if (!o) setAlignPhoto(null);
        }}
        layout={selectedLayout}
        initialPhoto={alignPhoto ? {
          url: fileUrl(alignPhoto.url),
          id: alignPhoto.id,
          // Pass any previously-saved alignment so the dialog opens on
          // those frames rather than the layout's pristine ones — lets
          // the engineer fine-tune an existing alignment instead of
          // starting from scratch every time.
          adjustedFrames: alignPhoto.adjustedFrames || null,
        } : null}
        onCaptured={(meta, alignment) => {
          if (alignPhoto) {
            // Adjust-existing flow — patch the photo's adjustedFrames in place.
            setPhotos((prev) => prev.map((p) =>
              p.id === alignPhoto.id
                ? { ...p, adjustedFrames: alignment?.adjustedFrames || null }
                : p
            ));
          } else {
            setPhotos((prev) => [
              ...prev,
              { ...meta, adjustedFrames: alignment?.adjustedFrames || null },
            ]);
          }
        }}
      />

      {/* Step 2: Site / inspector */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h2 className="font-display uppercase tracking-wide text-slate-900 mb-4">2. Certificate details</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Certificate type</Label>
            <Select value={form.certType} onValueChange={v => upd('certType', v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="EICR">EICR — Electrical Installation Condition Report</SelectItem>
                <SelectItem value="MWC">MWC — Minor Electrical Installation Works</SelectItem>
                <SelectItem value="DEIC">DEIC — Domestic Electrical Installation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Customer</Label>
            <Select value={form.customerId || 'all'} onValueChange={v => {
              const next = v === 'all' ? '' : v;
              upd('customerId', next);
              // Always reset the site when the customer changes — the
              // current site might not belong to the new customer.
              if (form.siteId) upd('siteId', '');
            }}>
              <SelectTrigger className="mt-1.5" data-testid="cert-customer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">— All customers —</SelectItem>
                {customerOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Site *</Label>
            <Select value={form.siteId} onValueChange={v => {
              const s = sites.find(x => x.id === v);
              upd('siteId', v);
              if (s && !form.siteAddress) upd('siteAddress', s.address || s.name);
              // Auto-fill the customer when the engineer picks a
              // site directly without scoping by customer first.
              if (s && s.customerId && !form.customerId) upd('customerId', s.customerId);
            }}>
              <SelectTrigger className="mt-1.5" data-testid="cert-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                {(form.customerId
                  ? sites.filter(s => s.customerId === form.customerId)
                  : sites
                ).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.customerId && sites.filter(s => s.customerId === form.customerId).length === 0 && (
              <p className="text-[11px] text-amber-700 mt-1">This customer has no sites yet.</p>
            )}
          </div>
          <div className="md:col-span-2"><Label>Site address</Label><div className="mt-1.5">
            <UkAddressFields
              value={form.siteAddressParts}
              legacyText={form.siteAddress}
              onChange={(parts) => { upd('siteAddressParts', parts); upd('siteAddress', formatAddress(parts, ', ')); }}
              testId="cert-site-address"
            />
          </div></div>
          <div><Label>Client</Label><Input className="mt-1.5" value={form.clientName} onChange={e => upd('clientName', e.target.value)} /></div>
          <div><Label>Date of inspection</Label><Input className="mt-1.5" type="date" value={form.dateOfInspection} onChange={e => upd('dateOfInspection', e.target.value)} /></div>
          <div><Label>Inspecting engineer</Label><Input className="mt-1.5" value={form.inspectorName} onChange={e => upd('inspectorName', e.target.value)} /></div>
          <div><Label>Reg No. (NICEIC / ECA)</Label><Input className="mt-1.5" value={form.inspectorRegNumber} onChange={e => upd('inspectorRegNumber', e.target.value)} /></div>
          <div><Label>Next inspection due</Label><Input className="mt-1.5" type="date" value={form.nextInspectionDate} onChange={e => upd('nextInspectionDate', e.target.value)} /></div>
          <div>
            <Label>Overall condition</Label>
            <Select value={form.overallCondition} onValueChange={v => upd('overallCondition', v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Satisfactory">Satisfactory</SelectItem>
                <SelectItem value="Unsatisfactory">Unsatisfactory</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label>Supply type</Label>
            <Select value={form.supplyType} onValueChange={v => upd('supplyType', v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TN-S">TN-S</SelectItem>
                <SelectItem value="TN-C-S">TN-C-S (PME)</SelectItem>
                <SelectItem value="TT">TT</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nominal voltage</Label><Input className="mt-1.5" value={form.nominalVoltage} onChange={e => upd('nominalVoltage', e.target.value)} /></div>
          <div><Label>Nominal frequency</Label><Input className="mt-1.5" value={form.nominalFrequency} onChange={e => upd('nominalFrequency', e.target.value)} /></div>
          <div><Label>Earth-fault loop Ze</Label><Input className="mt-1.5" value={form.earthFaultLoopImpedance} onChange={e => upd('earthFaultLoopImpedance', e.target.value)} placeholder="0.35 Ω" /></div>
          <div><Label>Prospective fault current Ipf</Label><Input className="mt-1.5" value={form.prospectiveFaultCurrent} onChange={e => upd('prospectiveFaultCurrent', e.target.value)} placeholder="1.6 kA" /></div>
        </div>
        <div className="mt-4">
          <Label>Observations &amp; recommendations</Label>
          <Textarea rows={4} className="mt-1.5" value={form.observations} onChange={e => upd('observations', e.target.value)} />
        </div>
      </section>

      {/* Step 3: Circuits */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display uppercase tracking-wide text-slate-900">3. Circuit test results</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleApplyRulesManual}
              data-testid="apply-rules-btn"
              disabled={activeRuleCount === 0}
              title={
                activeRuleCount === 0
                  ? 'No rules defined yet — create some in Lists → Cert Rules'
                  : `Run ${activeRuleCount ?? ''} active rule${activeRuleCount === 1 ? '' : 's'} over every circuit and fill empty cable size / notes fields`
              }
            >
              <Wand2 className="h-4 w-4 mr-1" />
              {activeRuleCount === 0 ? 'No rules' : `Apply rules${activeRuleCount != null ? ` (${activeRuleCount})` : ''}`}
            </Button>
            <Button variant="outline" size="sm" onClick={addCircuit}><Plus className="h-4 w-4 mr-1" /> Add circuit</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-left">
                {['#', '', 'Description', 'Cable', 'MCB', 'Rating', 'RCD', 'IR (MΩ)', 'Zs (Ω)', 'RCD trip', 'Pol.', ''].map(h => <th key={h} className="px-2 py-2 font-medium text-slate-600">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {form.circuits.map((c, i) => (
                <tr key={i}>
                  <td className="px-2 py-1 w-12"><Input value={c.number} onChange={e => updCircuit(i, 'number', e.target.value)} className="h-8 text-xs" /></td>
                  <td className="px-1 py-1 w-9 text-center">
                    <CertCircuitCropPopover
                      layoutId={selectedLayoutId}
                      frameId={c.frameId}
                      aiSnapshot={aiOriginal[i] || null}
                      currentValues={c}
                      label={c.number || `row-${i + 1}`}
                    />
                  </td>
                  <td className="px-2 py-1"><Input value={c.description} onChange={e => updCircuit(i, 'description', e.target.value)} className="h-8 text-xs" placeholder="e.g. Lighting GF" /></td>
                  <td className="px-2 py-1 w-28"><Input value={c.cableSize} onChange={e => updCircuit(i, 'cableSize', e.target.value)} className="h-8 text-xs" placeholder="1.5 mm²" /></td>
                  <td className="px-2 py-1 w-20">
                    <Select value={c.mcbType || 'B'} onValueChange={v => updCircuit(i, 'mcbType', v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="B">B</SelectItem><SelectItem value="C">C</SelectItem><SelectItem value="D">D</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1 w-24"><Input value={c.mcbRating} onChange={e => updCircuit(i, 'mcbRating', e.target.value)} className="h-8 text-xs" placeholder="32A" /></td>
                  <td className="px-2 py-1 w-24"><Input value={c.rcdRating} onChange={e => updCircuit(i, 'rcdRating', e.target.value)} className="h-8 text-xs" /></td>
                  <td className="px-2 py-1 w-24"><Input value={c.insulationResistance} onChange={e => updCircuit(i, 'insulationResistance', e.target.value)} className="h-8 text-xs" /></td>
                  <td className="px-2 py-1 w-24"><Input value={c.earthLoopImpedance} onChange={e => updCircuit(i, 'earthLoopImpedance', e.target.value)} className="h-8 text-xs" /></td>
                  <td className="px-2 py-1 w-24"><Input value={c.rcdTripTime} onChange={e => updCircuit(i, 'rcdTripTime', e.target.value)} className="h-8 text-xs" /></td>
                  <td className="px-2 py-1 w-20">
                    <Select value={c.polarity || 'Pass'} onValueChange={v => updCircuit(i, 'polarity', v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Pass">Pass</SelectItem><SelectItem value="Fail">Fail</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1">
                    <Button variant="ghost" size="icon" onClick={() => removeCircuit(i)} className="h-7 w-7 text-slate-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Save */}
      <div className="flex items-center justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(`${base}/certificates`)}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
          Save as draft
        </Button>
      </div>
    </div>
  );
};

export default CertNew;
